import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger } from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { DocumentSelectionType } from '../../../../components/Dropdown/DocumentDropdown'
import * as Yup from 'yup'
import DocumentCombobox, { DocumentComboboxProps } from '../../Combobox/DocumentCombobox'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { FormikConfig } from 'formik/dist/types'
import { fetchFolderDriveItemTree, FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import { IFileDto } from '../../../../config/fileConfig'
import FileCombobox from '../../../../components/Combobox/FileCombobox'
import { FileListItemDto } from '../../../../context/FileContext'
import { Destination } from '../../../../context/AxiosContext'
import useSharePoint from '../../../../hooks/useSharePoint'
import { AxiosResponse } from 'axios'
import { formatInputDocumentName } from '../../../../helpers/regexHelper'
import FormikInputField from '../../../../components/Formik/FormikInputField'

export const getMutateDocumentDialogBodySchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        fileId: Yup.string().required(translate('file.pivot.document.create.fileid.required')),
        folderId: Yup.string().required(translate('file.pivot.document.create.folderid.required')),
        name: Yup.string()
            .required(translate('file.pivot.document.create.name.required'))
            .test('is-valid-name', translate('file.pivot.document.create.name.invalid'), value => !value.match(/^\./)),
    })
}

export enum MutateDocumentAction {
    Rename = 'RENAME',
    Copy = 'COPY',
    Move = 'MOVE',
}

export type MutateDocumentDialogBodyProps = {
    items?: FolderDriveItem[]
    action?: MutateDocumentAction
    file: IFileDto
    folders: FolderDriveItem[]
    callback?: (addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) => void
}

export type MutateDocumentDialogBodyValues = {
    name: string
    fileId: string
    folderId: string
}

export default function MutateDocumentDialogBody(
    props: HammockFormDialogProps<MutateDocumentDialogBodyValues, MutateDocumentDialogBodyProps>,
): ReactElement {
    const axios = useAxios()
    const { sharePoint } = useSharePoint()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const { initialValues, setFieldValue, setFieldError, isSubmitting } = useFormikContext<MutateDocumentDialogBodyValues>()

    const dialogFormBodyProps = useMemo(() => props.dialogFormBodyProps() ?? ({} as MutateDocumentDialogBodyProps), [props])
    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const titleKey = useMemo(() => {
        switch (dialogFormBodyProps.action) {
            case MutateDocumentAction.Rename:
                return `file.pivot.document.mutate.rename.title.document`
            case MutateDocumentAction.Copy:
                return dialogFormBodyProps.items.length > 1
                    ? `file.pivot.document.mutate.copy.title.multiple`
                    : `file.pivot.document.mutate.copy.title.document`
            case MutateDocumentAction.Move:
                return dialogFormBodyProps.items.length > 1
                    ? `file.pivot.document.mutate.move.title.multiple`
                    : `file.pivot.document.mutate.move.title.document`
        }
    }, [])

    const [selectedFile, setSelectedFile] = useState<FileListItemDto>(dialogFormBodyProps.file)
    const [folders, setFolders] = useState<FolderDriveItem[]>(dialogFormBodyProps.folders)
    useEffect(() => {
        if (selectedFile.id !== initialValues.fileId) {
            fetchFolderDriveItemTree(axios, selectedFile.documentRootId, true)
                .then(result => setFolders(result))
                .catch(e => {
                    setFolders([])
                    if (e.status === 404) {
                        setFieldError('fileId', translate('file.pivot.document.mutate.form.fileid.notfound'))
                    } else {
                        setFieldError('fileId', e.message)
                    }
                })
        }
        if (selectedFile.id === initialValues.fileId) {
            setFolders(dialogFormBodyProps.folders)
        }
    }, [selectedFile])

    const handleSubmit = useCallback<FormikConfig<MutateDocumentDialogBodyValues>['onSubmit']>(
        values => {
            switch (dialogFormBodyProps.action) {
                case MutateDocumentAction.Move: {
                    return Promise.all(
                        dialogFormBodyProps.items
                            .map(item =>
                                axios
                                    .patch<DriveItem, AxiosResponse<DriveItem>>(
                                        `/items/${item.id}`,
                                        { parentReference: { driveId: sharePoint.driveId, id: values.folderId } },
                                        {
                                            baseURL: Destination.Documents,
                                            params: { '@microsoft.graph.conflictBehavior': 'rename' },
                                            headers: { SuppressErrors: 'true' },
                                        },
                                    )
                                    .then<FolderDriveItem>(result => ({ ...item, ...result.data })),
                            )
                            .filter(Boolean),
                    ).then(() => {
                        dialogFormBodyProps.callback?.(values.fileId === initialValues.fileId ? undefined : [], [])
                    })
                }
                case MutateDocumentAction.Copy: {
                    return Promise.all(
                        dialogFormBodyProps.items.map(item =>
                            axios.post<DriveItem, AxiosResponse<DriveItem>>(
                                `/items/${item.id}/copy`,
                                { parentReference: { driveId: sharePoint.driveId, id: values.folderId } },
                                {
                                    baseURL: Destination.Documents,
                                    params: { '@microsoft.graph.conflictBehavior': 'rename' },
                                    headers: { SuppressErrors: 'true' },
                                },
                            ),
                        ),
                    ).then(() => {
                        dialogFormBodyProps.callback?.(values.fileId === initialValues.fileId ? undefined : [], [])
                    })
                }
                default: {
                    return Promise.all(
                        dialogFormBodyProps.items.map(item =>
                            axios
                                .patch<DriveItem, AxiosResponse<DriveItem>>(
                                    `/items/${item.id}`,
                                    { name: values.name },
                                    {
                                        baseURL: Destination.Documents,
                                        params: { '@microsoft.graph.conflictBehavior': 'rename' },
                                        headers: { SuppressErrors: 'true' },
                                    },
                                )
                                .then<FolderDriveItem>(result => ({ ...item, ...result.data })),
                        ),
                    ).then(() => dialogFormBodyProps.callback?.(undefined, []))
                }
            }
        },
        [sharePoint],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    return (
        <DialogBody>
            <DialogTitle>{translate(titleKey)}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    {dialogFormBodyProps.action === MutateDocumentAction.Rename && (
                        <FormikInputField
                            required
                            labelKey={'file.pivot.document.mutate.form.name'}
                            orientation={viewportSmall ? 'vertical' : 'horizontal'}
                            fieldName={'name'}
                            componentProps={{
                                placeholder: translate('file.pivot.document.mutate.form.name'),
                                tabIndex: dialogFormBodyProps.action === MutateDocumentAction.Rename ? 1 : -1,
                                onBlur: e => setFieldValue('name', formatInputDocumentName(e.currentTarget.value)),
                                onKeyDown: e => {
                                    if (e.key === 'Enter') {
                                        setFieldValue('name', formatInputDocumentName(e.currentTarget.value))
                                    }
                                },
                            }}
                        />
                    )}
                    {dialogFormBodyProps.action !== MutateDocumentAction.Rename && (
                        <FormikComboboxField
                            required
                            labelKey={'file.pivot.document.mutate.form.fileid'}
                            orientation={viewportSmall ? 'vertical' : 'horizontal'}
                            fieldName={'fileId'}
                            component={FileCombobox}
                            componentProps={{
                                onOptionSelect: (_e, data, selectedEntities) => {
                                    setFieldError('fileId', undefined)
                                    setFieldValue('fileId', data.selectedOptions?.[0])
                                    setSelectedFile(selectedEntities?.[0])
                                },
                                tabIndex: -1,
                            }}
                        />
                    )}
                    {dialogFormBodyProps.action !== MutateDocumentAction.Rename && (
                        <FormikComboboxField<MutateDocumentDialogBodyValues, DocumentComboboxProps>
                            required
                            labelKey={'file.pivot.document.mutate.form.folderid'}
                            orientation={viewportSmall ? 'vertical' : 'horizontal'}
                            fieldName={'folderId'}
                            component={DocumentCombobox}
                            componentProps={{
                                selectionType: DocumentSelectionType.FolderOnly,
                                entities: folders,
                                tabIndex: dialogFormBodyProps.action === MutateDocumentAction.Move ? 1 : 0,
                            }}
                        />
                    )}
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button appearance="primary" type={'submit'} disabled={isSubmitting}>
                    {translate(`file.pivot.document.mutate.${dialogFormBodyProps.action.toLowerCase()}.button`)}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary" disabled={isSubmitting}>
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
